/* eslint-disable react/jsx-props-no-spreading */
import { arrayOf, bool, func, node, number, oneOfType, shape, string } from 'prop-types';
import React, { Fragment, useMemo, useState, useRef } from 'react';
import { dt } from '../../helpers/Utils/GetTailwindToken';
import AccordionSection from './components/AccordionSection';

const Accordion = ({
    automationId,
    children,
    className,
    iconSize,
    onClick,
    panelLeftIndent,
    sectionUnderline,
    toggleControlLeft,
    toggleOnlyOne,
    userToggle,
    parentSection,
    dynamicAccordionSections,
    isGalleryFilter,
}) => {
    const [expanded, setExpanded] = useState([]);
    const isInitialLoad = useRef(true);

    useMemo(() => {
        const remapFiltersLogic = dynamicAccordionSections
            ? children.length !== 0
            : children.length !== 0 && children?.length !== expanded?.length;
        if (remapFiltersLogic) {
            let shouldExpand = false;
            let expandedCount = 0;

            const updatedExpanded = children.map((item) => {
                const itemKey = item?.key || item?.header;

                if (userToggle && isInitialLoad?.current) {
                    /*
                     * If userToggle is true and it's initial load
                     * don't expand any panels
                     */
                    return { key: itemKey, expanded: false };
                }
                if (toggleOnlyOne) {
                    /*
                     * If toggleOnlyOne is true and multiple panels have expanded
                     * set true then only expand the first expanded panel
                     */
                    if (shouldExpand) expandedCount += 1;
                    shouldExpand = expandedCount > 0 ? false : item?.expanded;

                    return { key: itemKey, expanded: shouldExpand };
                }

                if (userToggle) {
                    const itemExists = expanded.find(
                        (expandedItem) => expandedItem?.key === item?.key
                    );
                    return {
                        key: itemKey,
                        expanded: itemExists?.expanded || false,
                    };
                }

                /*
                 * default output
                 */
                return { key: itemKey, expanded: item?.expanded || false };
            });

            setExpanded(updatedExpanded);
            isInitialLoad.current = false;
        }
    }, [children]);

    return (
        <div
            className={className}
            data-automation-id={automationId}
            data-component-name="Accordion"
        >
            {children.map(({ header, panel, key = null }, index) => {
                const itemKey = key || header;

                const handleHeaderClick = (event) => {
                    onClick && onClick(event);
                    setExpanded((prevExpanded) => {
                        const updated = [...prevExpanded].map((item) => {
                            return item?.key === itemKey
                                ? { ...item, expanded: !item?.expanded }
                                : { ...item, expanded: toggleOnlyOne ? false : item.expanded };
                        });

                        return updated;
                    });
                };

                const findExpanded = () => {
                    let isExpanded = false;
                    const itemFound = expanded.find((item) => item?.key === itemKey);
                    if (itemFound) isExpanded = itemFound?.expanded;
                    return isExpanded;
                };

                /*
                 * Ran into an issue using a dynamic key causing AnimateHeight to
                 * not respect the duration prop. The key needs to be stable in order
                 * for AnimateHeight (and React in general) to function properly.
                 * Please see this link for additional reference:
                 * https://stackoverflow.com/questions/51371953/react-collapse-transition-doesnt-animate-when-wrapped-in-a-div-with-a-dynamic-k#answer-51380169
                 */
                // eslint-disable-next-line react/no-array-index-key
                return (
                    <Fragment key={index}>
                        <hr
                            className={
                                sectionUnderline
                                    ? dt([
                                          'my-0',
                                          'border-solid',
                                          'border-t',
                                          'border-b-0',
                                          'border-l-0',
                                          'border-r-0',
                                          'border-gray-15',
                                      ])
                                    : dt(['my-0', 'border-0'])
                            }
                        />
                        <AccordionSection
                            index={index}
                            isExpanded={findExpanded()}
                            onClick={handleHeaderClick}
                            toggleControlLeft={toggleControlLeft}
                            iconSize={iconSize}
                            panelLeftIndent={panelLeftIndent}
                            header={header}
                            panel={panel}
                            parentSection={parentSection}
                            isGalleryFilter={isGalleryFilter}
                        />
                    </Fragment>
                );
            })}
        </div>
    );
};

Accordion.propTypes = {
    /*
     * Children accepts an array of section objects.
     * For examples reference Storybook or accordion.stories.js.
     */
    children: arrayOf(
        shape({
            expanded: bool,
            header: oneOfType([node, number, string]).isRequired,
            panel: oneOfType([node, number, string]).isRequired,
            key: oneOfType([node, number, string]),
        })
    ).isRequired,
    /*
     * Automation testing ID.
     */
    automationId: string,
    /*
     * Allows custom classes to be applied.
     */
    className: string,
    /*
     * Sets size of toggle icon.
     */
    iconSize: string,
    /*
     * Allows custom additional functionality when opening and closing panels.
     */
    onClick: func,
    /*
     * If panelLeftIndent is false and toggleControlLeft is true,
     * panel is not indented to align with header.
     */
    panelLeftIndent: bool,
    /*
     * If sectionUnderline is false supress each section underline.
     */
    sectionUnderline: bool,
    /*
     * If toggleControlLeft is true the icon to expand/collapse sections
     * is rendered to the left of the header.
     */
    toggleControlLeft: bool,
    /*
     * If toggleOnlyOne is true only one section at a time can be expanded.
     * If multiple sections have the 'expanded' option set to true, only
     * the first of those sections is expanded when Accordion is rendered.
     */
    toggleOnlyOne: bool,
    /*
     * If userToggle is true default toggle should be false and on true when user toggles themselves
     */
    userToggle: bool,
    parentSection: string,
    /*
     * if number of accordion sections changes depending on selections then bool is true
     */
    dynamicAccordionSections: bool,
};

Accordion.defaultProps = {
    automationId: null,
    className: null,
    iconSize: '24',
    onClick: () => {},
    panelLeftIndent: true,
    sectionUnderline: true,
    toggleControlLeft: false,
    toggleOnlyOne: false,
    userToggle: false,
    parentSection: '',
    dynamicAccordionSections: false,
};

export default Accordion;
